/**
 * MockInterviewPanel.js
 * AI-powered mock interview panel — Phase 3, Sprint 7.
 * 
 * Features:
 *  - Role + interview type selector (Technical / Behavioral / HR / System Design)
 *  - AI generates 5–8 realistic questions based on resume + role
 *  - User types/records answer → AI scores it + gives feedback
 *  - Session score tracker with per-question breakdown
 *  - "Next Question" flow
 *
 * Props:
 *   theme       — 'dark' | 'light'
 *   resumeData  — current resume JSON
 *   authToken   — auth token for API calls
 *   onSendToChat — callback to fire a prompt in the main chat
 */
import React, { useState, useRef } from 'react';

const INTERVIEW_TYPES = [
  { id: 'behavioral', emoji: '🧠', label: 'Behavioral',  desc: 'STAR-format situational questions' },
  { id: 'technical',  emoji: '💻', label: 'Technical',   desc: 'Coding & system concepts' },
  { id: 'hr',         emoji: '🤝', label: 'HR Round',    desc: 'Culture fit & motivation' },
  { id: 'system',     emoji: '⚙️',  label: 'System Design', desc: 'Architecture & scalability' },
];

const SCORE_COLOR = s => s >= 80 ? '#22c55e' : s >= 60 ? '#3b82f6' : s >= 40 ? '#f59e0b' : '#ef4444';

function ScoreBadge({ score, size = 36 }) {
  const color = SCORE_COLOR(score);
  const R = (size / 2) - 4;
  const C = 2 * Math.PI * R;
  const offset = C - (score / 100) * C;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={R} fill="none" strokeWidth="3.5"
        stroke="rgba(255,255,255,0.07)" />
      <circle cx={size/2} cy={size/2} r={R} fill="none" strokeWidth="3.5"
        stroke={color} strokeLinecap="round"
        strokeDasharray={C} strokeDashoffset={offset}
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em"
        fontSize={size * 0.28} fontWeight="800" fill={color}>
        {score}
      </text>
    </svg>
  );
}

export default function MockInterviewPanel({ theme = 'dark', resumeData, authToken, onSendToChat }) {
  const T = theme === 'dark';
  const [interviewType, setInterviewType] = useState('behavioral');
  const [targetRole, setTargetRole] = useState('');
  const [phase, setPhase] = useState('setup'); // setup | loading | active | feedback | summary
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState(null); // { score, strengths, improvements, model_answer }
  const [sessionResults, setSessionResults] = useState([]); // [{q, a, feedback}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const textareaRef = useRef(null);

  const authHdr = { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` };

  const startSession = async () => {
    if (!targetRole.trim()) { setError('Please enter a target role.'); return; }
    setLoading(true); setError(''); setPhase('loading');
    try {
      const res = await fetch('/api/interview/generate-questions', {
        method: 'POST', headers: authHdr,
        body: JSON.stringify({
          interview_type: interviewType,
          target_role: targetRole,
          resume_summary: resumeData?.summaryText || '',
          skills: resumeData?.skills || [],
          experience: (resumeData?.experience || []).slice(0, 3).map(e => e.role || e.title || ''),
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to generate questions.');
      setQuestions(d.questions || []);
      setCurrentIdx(0);
      setSessionResults([]);
      setAnswer('');
      setFeedback(null);
      setPhase('active');
    } catch (e) {
      // Fallback: use AI chat to generate mock questions
      const fallback = generateFallbackQuestions(interviewType, targetRole);
      setQuestions(fallback);
      setCurrentIdx(0);
      setSessionResults([]);
      setAnswer('');
      setFeedback(null);
      setPhase('active');
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackQuestions = (type, role) => {
    const behavioral = [
      `Tell me about yourself and why you're interested in the ${role} role.`,
      `Describe a challenging project you led. What was your approach and outcome?`,
      `Give an example of a time you had to work with a difficult team member. How did you handle it?`,
      `Tell me about a time you failed. What did you learn from it?`,
      `Where do you see yourself in 5 years?`,
    ];
    const technical = [
      `Explain the difference between a stack and a queue. When would you use each?`,
      `What is Big O notation? Give an example of O(n log n).`,
      `Describe the MVC architecture pattern and its advantages.`,
      `How would you optimize a slow SQL query?`,
      `Explain RESTful API design principles.`,
    ];
    const hr = [
      `Why do you want to leave your current company?`,
      `What are your salary expectations for this role?`,
      `Why should we hire you over other candidates?`,
      `How do you handle pressure and tight deadlines?`,
      `What is your greatest professional strength?`,
    ];
    const system = [
      `Design a URL shortening service like bit.ly.`,
      `How would you design a scalable notification system?`,
      `Design a rate limiter for an API.`,
      `How would you build a distributed cache?`,
      `Design an e-commerce order management system.`,
    ];
    const map = { behavioral, technical, hr, system };
    return (map[type] || behavioral).map((q, i) => ({ id: i+1, question: q }));
  };

  const submitAnswer = async () => {
    if (answer.trim().length < 20) { setError('Please write a more complete answer (min 20 chars).'); return; }
    setLoading(true); setError('');
    const q = questions[currentIdx];
    try {
      const res = await fetch('/api/interview/evaluate-answer', {
        method: 'POST', headers: authHdr,
        body: JSON.stringify({
          question: q.question,
          answer,
          interview_type: interviewType,
          target_role: targetRole,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Evaluation failed.');
      setFeedback(d);
      setSessionResults(prev => [...prev, { question: q.question, answer, feedback: d }]);
      setPhase('feedback');
    } catch (e) {
      // Offline fallback scoring
      const wordCount = answer.trim().split(/\s+/).length;
      const score = Math.min(95, Math.max(30, 40 + wordCount * 0.8 + (answer.includes('%') ? 10 : 0) + (answer.includes('result') || answer.includes('outcome') ? 8 : 0)));
      const fallbackFeedback = {
        score: Math.round(score),
        strengths: ['You provided a response to the question.', answer.length > 200 ? 'Good detail level.' : 'Keep answers more detailed.'],
        improvements: ['Use the STAR format (Situation, Task, Action, Result).', 'Add specific metrics and outcomes.', 'Practice more concise delivery.'],
        model_answer: `A strong answer for this question would include: specific context about the situation, your unique contribution, concrete actions taken, and measurable results achieved.`,
      };
      setFeedback(fallbackFeedback);
      setSessionResults(prev => [...prev, { question: q.question, answer, feedback: fallbackFeedback }]);
      setPhase('feedback');
    } finally {
      setLoading(false);
    }
  };

  const nextQuestion = () => {
    if (currentIdx < questions.length - 1) {
      setCurrentIdx(idx => idx + 1);
      setAnswer('');
      setFeedback(null);
      setError('');
      setPhase('active');
      setTimeout(() => textareaRef.current?.focus(), 100);
    } else {
      setPhase('summary');
    }
  };

  const avgScore = sessionResults.length > 0
    ? Math.round(sessionResults.reduce((s, r) => s + (r.feedback?.score || 0), 0) / sessionResults.length)
    : 0;

  const card = { background: T ? 'rgba(255,255,255,0.04)' : '#fff',
    border: `1px solid ${T ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
    borderRadius: 16, padding: '16px',
    boxShadow: T ? '0 4px 20px rgba(0,0,0,0.3)' : '0 1px 8px rgba(0,0,0,0.06)' };

  // ── SETUP PHASE ─────────────────────────────────────────────────────────────
  if (phase === 'setup' || phase === 'loading') return (
    <div style={{ padding: '20px', height: '100%', overflowY: 'auto', background: T ? '#0b0b14' : '#f0f2f5' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(99,102,241,0.15)',
          border: '1px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 20 }}>🎤</span>
        </div>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0, color: T ? 'rgba(255,255,255,0.92)' : '#0f172a' }}>
            Mock Interview
          </h2>
          <p style={{ fontSize: 10, color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: '2px 0 0' }}>
            AI-powered interview practice with instant feedback
          </p>
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 8, fontWeight: 800, padding: '3px 8px', borderRadius: 20,
          background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white' }}>BETA</span>
      </div>

      {/* Interview Type */}
      <div style={{ ...card, marginBottom: 12 }}>
        <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
          color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: '0 0 12px' }}>Interview Type</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {INTERVIEW_TYPES.map(t => (
            <button key={t.id} onClick={() => setInterviewType(t.id)} style={{
              padding: '11px 12px', borderRadius: 12, textAlign: 'left', cursor: 'pointer',
              border: `1.5px solid ${interviewType === t.id ? '#6366f1' : T ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              background: interviewType === t.id ? 'rgba(99,102,241,0.12)' : T ? 'rgba(255,255,255,0.03)' : '#f8fafc',
              boxShadow: interviewType === t.id ? '0 0 0 2px rgba(99,102,241,0.2)' : 'none',
            }}>
              <div style={{ fontSize: 18, marginBottom: 4 }}>{t.emoji}</div>
              <p style={{ fontSize: 11.5, fontWeight: 700, color: interviewType === t.id ? '#6366f1' : T ? 'rgba(255,255,255,0.8)' : '#0f172a', margin: '0 0 2px' }}>{t.label}</p>
              <p style={{ fontSize: 9.5, color: T ? 'rgba(255,255,255,0.35)' : '#94a3b8', margin: 0 }}>{t.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Target Role */}
      <div style={{ ...card, marginBottom: 12 }}>
        <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
          color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: '0 0 8px' }}>Target Role</p>
        <input
          value={targetRole}
          onChange={e => { setTargetRole(e.target.value); if (error) setError(''); }}
          placeholder="e.g. Software Engineer, Product Manager, Data Analyst..."
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 12,
            border: `1px solid ${T ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            background: T ? 'rgba(255,255,255,0.04)' : '#f8fafc',
            color: T ? 'rgba(255,255,255,0.8)' : '#0f172a', outline: 'none', boxSizing: 'border-box' }}
        />
        {error && <p style={{ fontSize: 10, color: '#ef4444', margin: '6px 0 0', fontWeight: 600 }}>{error}</p>}
      </div>

      {/* Start Button */}
      <button onClick={startSession} disabled={phase === 'loading' || !targetRole.trim()} style={{
        width: '100%', padding: '13px', borderRadius: 12, border: 'none',
        background: !targetRole.trim() ? (T ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)') : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
        color: !targetRole.trim() ? (T ? 'rgba(255,255,255,0.3)' : '#94a3b8') : 'white',
        fontSize: 13, fontWeight: 700, cursor: !targetRole.trim() ? 'not-allowed' : 'pointer',
        boxShadow: targetRole.trim() ? '0 4px 18px rgba(99,102,241,0.35)' : 'none',
      }}>
        {phase === 'loading' ? '⏳ Generating Questions...' : '🎤 Start Mock Interview'}
      </button>

      {/* Info */}
      <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 12,
        background: T ? 'rgba(99,102,241,0.07)' : 'rgba(99,102,241,0.04)',
        border: '1px solid rgba(99,102,241,0.2)' }}>
        <p style={{ fontSize: 10.5, color: T ? 'rgba(255,255,255,0.5)' : '#475569', margin: 0, lineHeight: 1.5 }}>
          ✨ You'll get <strong style={{ color: T ? 'rgba(255,255,255,0.8)' : '#0f172a' }}>5–8 tailored questions</strong> based on your resume and target role. 
          Each answer gets scored with <strong style={{ color: T ? 'rgba(255,255,255,0.8)' : '#0f172a' }}>AI feedback</strong> on strengths, improvements, and a model answer.
        </p>
      </div>
    </div>
  );

  // ── ACTIVE QUESTION PHASE ────────────────────────────────────────────────────
  if (phase === 'active') {
    const q = questions[currentIdx];
    const progress = ((currentIdx) / questions.length) * 100;
    return (
      <div style={{ padding: '20px', height: '100%', overflowY: 'auto', background: T ? '#0b0b14' : '#f0f2f5', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Progress */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <div style={{ flex: 1, height: 4, borderRadius: 2, background: T ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg,#6366f1,#8b5cf6)', borderRadius: 2, transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, color: T ? 'rgba(255,255,255,0.45)' : '#94a3b8', flexShrink: 0 }}>
            {currentIdx + 1}/{questions.length}
          </span>
        </div>

        {/* Question card */}
        <div style={{ ...card }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(99,102,241,0.15)',
              border: '1px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, fontSize: 12, fontWeight: 800, color: '#6366f1' }}>
              Q{currentIdx + 1}
            </div>
            <p style={{ fontSize: 13, fontWeight: 600, color: T ? 'rgba(255,255,255,0.88)' : '#0f172a', margin: 0, lineHeight: 1.5 }}>
              {q?.question}
            </p>
          </div>
        </div>

        {/* Answer input */}
        <div style={{ ...card }}>
          <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
            color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: '0 0 8px' }}>Your Answer</p>
          <textarea
            ref={textareaRef}
            value={answer}
            onChange={e => { setAnswer(e.target.value); if (error) setError(''); }}
            placeholder="Type your answer here... (Try to use the STAR format: Situation, Task, Action, Result)"
            rows={7}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 11.5,
              border: `1px solid ${answer.length > 50 ? '#6366f1' : T ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              background: T ? 'rgba(255,255,255,0.04)' : '#f8fafc',
              color: T ? 'rgba(255,255,255,0.8)' : '#0f172a', outline: 'none',
              resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6,
              fontFamily: "'Inter',sans-serif", transition: 'border-color 0.2s' }}
          />
          {answer.length > 0 && (
            <p style={{ fontSize: 9, color: T ? 'rgba(255,255,255,0.3)' : '#94a3b8', margin: '4px 0 0', textAlign: 'right' }}>
              {answer.trim().split(/\s+/).length} words
            </p>
          )}
          {error && <p style={{ fontSize: 10, color: '#ef4444', margin: '4px 0 0', fontWeight: 600 }}>{error}</p>}
        </div>

        <button onClick={submitAnswer} disabled={loading || answer.trim().length < 20} style={{
          width: '100%', padding: '12px', borderRadius: 11, border: 'none',
          background: (loading || answer.trim().length < 20) ? (T ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)') : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
          color: (loading || answer.trim().length < 20) ? (T ? 'rgba(255,255,255,0.3)' : '#94a3b8') : 'white',
          fontSize: 12.5, fontWeight: 700, cursor: answer.trim().length < 20 ? 'not-allowed' : 'pointer',
          boxShadow: answer.trim().length >= 20 ? '0 4px 16px rgba(99,102,241,0.3)' : 'none',
        }}>
          {loading ? '⏳ Evaluating...' : '✅ Submit & Get Feedback'}
        </button>
      </div>
    );
  }

  // ── FEEDBACK PHASE ───────────────────────────────────────────────────────────
  if (phase === 'feedback' && feedback) {
    const color = SCORE_COLOR(feedback.score);
    return (
      <div style={{ padding: '20px', height: '100%', overflowY: 'auto', background: T ? '#0b0b14' : '#f0f2f5', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Score */}
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 16 }}>
          <ScoreBadge score={feedback.score} size={72} />
          <div>
            <p style={{ fontSize: 14, fontWeight: 800, color, margin: '0 0 4px' }}>
              {feedback.score >= 80 ? 'Excellent Answer! 🎉' : feedback.score >= 60 ? 'Good Answer 👍' : 'Needs Improvement ⚡'}
            </p>
            <p style={{ fontSize: 10, color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: 0 }}>
              Question {currentIdx + 1} of {questions.length}
            </p>
          </div>
        </div>

        {/* Strengths */}
        {feedback.strengths?.length > 0 && (
          <div style={{ ...card, background: T ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.04)', borderColor: 'rgba(34,197,94,0.2)' }}>
            <p style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#22c55e', margin: '0 0 10px' }}>✅ Strengths</p>
            {feedback.strengths.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 11, color: '#22c55e', flexShrink: 0 }}>•</span>
                <p style={{ fontSize: 11, color: T ? 'rgba(255,255,255,0.65)' : '#475569', margin: 0, lineHeight: 1.4 }}>{s}</p>
              </div>
            ))}
          </div>
        )}

        {/* Improvements */}
        {feedback.improvements?.length > 0 && (
          <div style={{ ...card, background: T ? 'rgba(245,158,11,0.06)' : 'rgba(245,158,11,0.04)', borderColor: 'rgba(245,158,11,0.2)' }}>
            <p style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#f59e0b', margin: '0 0 10px' }}>⚡ Improvements</p>
            {feedback.improvements.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 11, color: '#f59e0b', flexShrink: 0 }}>→</span>
                <p style={{ fontSize: 11, color: T ? 'rgba(255,255,255,0.65)' : '#475569', margin: 0, lineHeight: 1.4 }}>{s}</p>
              </div>
            ))}
          </div>
        )}

        {/* Model answer */}
        {feedback.model_answer && (
          <div style={{ ...card, background: T ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.04)', borderColor: 'rgba(99,102,241,0.2)' }}>
            <p style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6366f1', margin: '0 0 8px' }}>💡 Model Answer Tip</p>
            <p style={{ fontSize: 11, color: T ? 'rgba(255,255,255,0.6)' : '#475569', margin: 0, lineHeight: 1.5 }}>{feedback.model_answer}</p>
          </div>
        )}

        <button onClick={nextQuestion} style={{
          width: '100%', padding: '12px', borderRadius: 11, border: 'none',
          background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white',
          fontSize: 12.5, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(99,102,241,0.3)',
        }}>
          {currentIdx < questions.length - 1 ? `Next Question (${currentIdx + 2}/${questions.length}) →` : '📊 View Session Summary →'}
        </button>
      </div>
    );
  }

  // ── SUMMARY PHASE ────────────────────────────────────────────────────────────
  if (phase === 'summary') {
    const color = SCORE_COLOR(avgScore);
    return (
      <div style={{ padding: '20px', height: '100%', overflowY: 'auto', background: T ? '#0b0b14' : '#f0f2f5', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Overall score */}
        <div style={{ ...card, textAlign: 'center', padding: '24px' }}>
          <ScoreBadge score={avgScore} size={80} />
          <p style={{ fontSize: 16, fontWeight: 800, color, margin: '12px 0 4px' }}>
            {avgScore >= 80 ? 'Interview Ready! 🎉' : avgScore >= 60 ? 'Good Performance 👍' : 'Keep Practicing ⚡'}
          </p>
          <p style={{ fontSize: 11, color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: 0 }}>
            Average score across {sessionResults.length} questions
          </p>
        </div>

        {/* Per-question scores */}
        <div style={{ ...card }}>
          <p style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px',
            color: T ? 'rgba(255,255,255,0.4)' : '#64748b', margin: '0 0 12px' }}>Question Breakdown</p>
          {sessionResults.map((r, i) => {
            const sc = r.feedback?.score || 0;
            const qColor = SCORE_COLOR(sc);
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 9.5, fontWeight: 700, color: '#6366f1', flexShrink: 0, minWidth: 20 }}>Q{i+1}</span>
                <div style={{ flex: 1, height: 6, borderRadius: 3, background: T ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${sc}%`, background: qColor, borderRadius: 3, transition: 'width 0.4s ease' }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: qColor, flexShrink: 0, minWidth: 30, textAlign: 'right' }}>{sc}</span>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={() => { setPhase('setup'); setQuestions([]); setSessionResults([]); }} style={{
            width: '100%', padding: '11px', borderRadius: 10, border: 'none',
            background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white',
            fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(99,102,241,0.3)',
          }}>🔄 New Interview Session</button>
          <button onClick={() => onSendToChat && onSendToChat(`I just completed a mock ${interviewType} interview for ${targetRole} role with an average score of ${avgScore}/100. Please give me personalized coaching tips to improve my interview performance.`)}
            style={{ width: '100%', padding: '11px', borderRadius: 10,
              border: `1px solid ${T ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              background: T ? 'rgba(255,255,255,0.04)' : '#f8fafc',
              color: T ? 'rgba(255,255,255,0.7)' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            💬 Get AI Coaching Tips
          </button>
        </div>
      </div>
    );
  }

  return null;
}
